import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { ApolloError } from "@apollo/client";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { App } from "../../types/app.type";
import AppFilters from "../AppFilters";
import { AppCard } from "./AppCard/AppCard";
import { LoadingGalleryOrganizationApps } from "./AppCard/LoadingGalleryOrganizationApps";

type Errors = {
  organizationAppsError?: ApolloError;
  publicAppsError?: ApolloError;
};

type Props = {
  apps: App[];
  loading: boolean;
  errors: Errors;
};

export const AppGalleryView = ({
  apps,
  loading,
  errors: { organizationAppsError, publicAppsError }
}: Props) => {
  const { filterName, filterShowPublicApps } = useSearch({
    from: "/_auth/_fixed-sidebar/_organization-access/organization/$organizationSlug/gallery"
  });
  const { toast } = useToast();

  const navigate = useNavigate();

  const filteredApps = apps.filter(
    ({ displayName }) =>
      !filterName ||
      displayName.toLocaleLowerCase().match(filterName.toLocaleLowerCase())
  );

  if (loading) return <LoadingGalleryOrganizationApps />;
  if (organizationAppsError) {
    toast({
      title: "Unable to load organization apps and subscription apps",
      description:
        "An error occurred while loading apps. Please try to refresh the page or report the issue."
    });
    console.error(organizationAppsError);
  }
  if (publicAppsError) {
    toast({
      title: "Unable to load public apps",
      description:
        "An error occurred while loading apps. Please try to refresh the page or report the issue."
    });
    console.error(organizationAppsError);
  }

  if (organizationAppsError && publicAppsError)
    return (
      <Alert
        title="Unable to load apps"
        description="An error occurred while loading apps. Please try to refresh the page or report the issue."
        data-testid="errorAlert"
      />
    );

  return (
    <>
      <div className="w-full mt-6 flex justify-between items-center">
        <Input
          placeholder="Search for app..."
          data-testid="appSearch"
          value={filterName ?? ""}
          onChange={(event) =>
            navigate({
              to: "/organization/$organizationSlug/gallery",
              params: (prev) => ({
                organizationSlug: prev.organizationSlug || ""
              }),
              search: (prev) => ({ ...prev, filterName: event.target.value })
            })
          }
          disabled={loading}
          className="max-w-sm"
        />
        <div className="flex items-center gap-2">
          {(filterName || filterShowPublicApps) && (
            <Link
              to="/organization/$organizationSlug/gallery"
              params={(prev) => ({
                organizationSlug: prev.organizationSlug || ""
              })}
            >
              <Button variant="ghost" size="sm" data-testid="resetFilters">
                Reset filters
              </Button>
            </Link>
          )}
          <AppFilters />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filteredApps.map((app) => {
          return <AppCard key={app.id} app={app} />;
        })}
      </div>
    </>
  );
};
